<template>
  <div class="cover-container w-100 mx-auto" id="main_page" style="overflow: hidden;">
    <!-- A-Frame AR 場景 -->
    <a-scene v-if="isAFrameLoaded" ref="arScene" tap-place landing-page xrextras-loading xrextras-runtime-error
      renderer="colorManagement:true" xrweb="allowedDevices: any" embedded vr-mode-ui="enabled: false">

      <!-- 載入音效資源（模型由 WebAssembly 管道動態載入，不使用 a-asset-item）-->
      <a-assets>
        <audio id="animationSound" :src="currentAudioUrl" preload="auto"></audio>
      </a-assets>

      <!-- AR 相機設定 -->
      <a-camera id="camera" position="0 8 0" raycaster="objects: .cantap" cursor="fuse: false; rayOrigin: mouse;">
      </a-camera>

      <!-- 方向光源 -->
      <a-entity light="type: directional; intensity: 0.8; castShadow: false"
        xrextras-attach="target: camera; offset: 8 15 4" position="1 4.3 2.5">
      </a-entity>

      <!-- 環境光源 -->
      <a-light type="ambient" intensity="0.5"></a-light>

      <!-- 可點擊的地面 - 使用 plane 並關閉深度寫入，不影響模型渲染 -->
      <a-plane id="ground" class="cantap" scale="1000 1000 1" rotation="-90 0 0" position="0 0 0"
        material="transparent: true; opacity: 0; depthWrite: false">
      </a-plane>
    </a-scene>
    <div class="hint" v-if="isShowHint">
      <img :src="hintManager[this.currentSpotData].hintURL" alt="">
      <!-- gif 本身只負責視覺，實際可點擊區域縮小成置中的圓形，避免 gif 空白邊緣也能點到 -->
      <div class="hand-cursor-wrap" :class="hintManager[this.currentSpotData].curserClass">
        <img src="@/assets/images/hand_cursor.gif" alt="" class="hand-cursor-gif" style="pointer-events: none;">
        <div class="hand-cursor-hit" id="hand-cursor" @click.stop="onHandCursorClick"></div>
      </div>
      <div class="hintword" :class="'circle' + hintManager[currentSpotData].hintIndex"
        v-html="languageTexts[currentLanguage]['hint' + hintManager[currentSpotData].hintIndex + 'Text']">
      </div>
    </div>

    <!-- Loading 動畫 -->
    <div class="model-loading" v-if="isLoading">
      <div class="loading-spinner"></div>
      <p>{{ languageTexts[currentLanguage].loadingText }}</p>
    </div>

    <TopNavigation :showIndexBtn="isShowIndexBtn" :showMapBtn="isShowMapBtn" :showDataBtn="isShowDataBtn"
      :currentLanguage="currentLanguage" @back-to-index="onBackToIndex" @back-to-map="onBackToMap"
      @open-data-page="onOpenDataPage" />
    <InfoPage :isVisible="isShowData" :currentWayspotData="currentWayspotData" @close="closeInfoPage" />
    <!-- 開發者模式面板 -->
    <div class="dev-toggle" v-if="devMode" @click="devPanelOpen = !devPanelOpen">⚙</div>

    <div class="dev-panel" v-if="devMode && devPanelOpen">
      <div class="dev-header">{{ currentSpotData }}</div>
      <div class="dev-label">縮放</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustScale(-0.5)">−−</button>
        <button class="dev-btn" @click="devAdjustScale(-0.1)">−</button>
        <span class="dev-value">{{ devCurrentScale.toFixed(1) }}</span>
        <button class="dev-btn" @click="devAdjustScale(0.1)">+</button>
        <button class="dev-btn" @click="devAdjustScale(0.5)">++</button>
      </div>
      <div class="dev-label">旋轉偏移</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustRotation(-30)">−30</button>
        <button class="dev-btn" @click="devAdjustRotation(-5)">−5</button>
        <span class="dev-value">{{ devCurrentRotation }}°</span>
        <button class="dev-btn" @click="devAdjustRotation(5)">+5</button>
        <button class="dev-btn" @click="devAdjustRotation(30)">+30</button>
      </div>
      <div class="dev-label">位置 X（左 − / 右 +）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustOffset('x', -0.2)">−−</button>
        <button class="dev-btn" @click="devAdjustOffset('x', -0.05)">−</button>
        <span class="dev-value">{{ devCurrentOffset.x.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjustOffset('x', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjustOffset('x', 0.2)">++</button>
      </div>
      <div class="dev-label">位置 Y（下 − / 上 +）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustOffset('y', -0.2)">−−</button>
        <button class="dev-btn" @click="devAdjustOffset('y', -0.05)">−</button>
        <span class="dev-value">{{ devCurrentOffset.y.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjustOffset('y', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjustOffset('y', 0.2)">++</button>
      </div>
      <div class="dev-label">位置 Z（前 − / 後 +）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustOffset('z', -0.2)">−−</button>
        <button class="dev-btn" @click="devAdjustOffset('z', -0.05)">−</button>
        <span class="dev-value">{{ devCurrentOffset.z.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjustOffset('z', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjustOffset('z', 0.2)">++</button>
      </div>
      <template v-if="spotDataManager[currentSpotData]?.videoUrl">
        <div class="dev-label">影片位置 X（左 − / 右 +）</div>
        <div class="dev-scale-row">
          <button class="dev-btn" @click="devAdjustVideoOffset('x', -0.2)">−−</button>
          <button class="dev-btn" @click="devAdjustVideoOffset('x', -0.05)">−</button>
          <span class="dev-value">{{ devCurrentVideoOffset.x.toFixed(2) }}</span>
          <button class="dev-btn" @click="devAdjustVideoOffset('x', 0.05)">+</button>
          <button class="dev-btn" @click="devAdjustVideoOffset('x', 0.2)">++</button>
        </div>
        <div class="dev-label">影片位置 Y（下 − / 上 +）</div>
        <div class="dev-scale-row">
          <button class="dev-btn" @click="devAdjustVideoOffset('y', -0.2)">−−</button>
          <button class="dev-btn" @click="devAdjustVideoOffset('y', -0.05)">−</button>
          <span class="dev-value">{{ devCurrentVideoOffset.y.toFixed(2) }}</span>
          <button class="dev-btn" @click="devAdjustVideoOffset('y', 0.05)">+</button>
          <button class="dev-btn" @click="devAdjustVideoOffset('y', 0.2)">++</button>
        </div>
        <div class="dev-label">影片位置 Z（前 − / 後 +）</div>
        <div class="dev-scale-row">
          <button class="dev-btn" @click="devAdjustVideoOffset('z', -0.2)">−−</button>
          <button class="dev-btn" @click="devAdjustVideoOffset('z', -0.05)">−</button>
          <span class="dev-value">{{ devCurrentVideoOffset.z.toFixed(2) }}</span>
          <button class="dev-btn" @click="devAdjustVideoOffset('z', 0.05)">+</button>
          <button class="dev-btn" @click="devAdjustVideoOffset('z', 0.2)">++</button>
        </div>
        <div class="dev-row">
          <button class="dev-btn" @click="devTestSpotVideo">立即測試影片位置</button>
        </div>
      </template>
      <div class="dev-row">
        <button class="dev-btn dev-save-btn" @click="devSave">儲存</button>
        <button class="dev-btn" @click="devExport">匯出 Config</button>
      </div>
      <div class="dev-row">
        <button class="dev-btn" @click="devClearOverrides">清除覆寫（改回讀 config）</button>
      </div>
      <div v-if="devSavedMsg" class="dev-saved-msg">{{ devSavedMsg }}</div>
      <textarea v-if="devExportText" class="dev-export-text" readonly :value="devExportText"
        @click="$event.target.select()"></textarea>
    </div>

    <div class="finish-page" id="finish-page" v-if="isShowFinalPage">
      <div class="finish-content">
        <h1>{{ languageTexts[currentLanguage].finishTitle }}</h1>
        <h1>{{ languageTexts[currentLanguage].finishSubtitle }}</h1>
        <div class="btn_area">
          <div class="btn_css" @click="reScanTarget()">
            {{ languageTexts[currentLanguage].rewatchButton }}
            <img src="@/assets/images/button-cover02.png" alt="" id="btn_cover">
          </div>
          <div class="btn_css" @click="nextPage()">
            {{ languageTexts[currentLanguage].nextButton }}
            <img src="@/assets/images/button-cover02.png" alt="" id="btn_cover">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import TopNavigation from '@/components/TopNavigation.vue'
import InfoPage from '@/components/InfoPage.vue'
import { isWasmSupported, loadWasmModule, clearWasmCache, fetchAndProcessModel } from '@/utils/wasmLoader'
import { wayspotDataManager, pointKeyMap } from '@/utils/wayspotDataManager'
import { withBase, getBasePath } from '@/utils/urlBase'

// 切換為 false 關閉開發者調整面板
const DEV_MODE = false

// 距離補償的基準距離（公尺）：模型在此距離下等比例為 1x
const SCALE_REFERENCE_DISTANCE = 1.5

// Google Analytics：每個景點對應一個自訂事件名稱，key 為 currentSpotData（即 ?point= 的值）
const GA_EVENT_NAME_MAP = {
  Areca: 'Areca_Nut_scan',
  Parasolleaftree: 'Blood_Tree_scan',
  Chinaberry: 'Chinaberry_scan',
  Barringtoniaasiatica: 'Fish_Poison_Tree_scan',
  LargeLeavedNanmu: 'Large_leaved_Machilus_scan',
  Mango: 'Mango_scan',
  VitexNigra: 'Negundo_Chaste_Tree_scan',
  PaperMulberry: 'Paper_Mulberry_scan',
  Soapberry: 'Soapberry_Tree_scan',
  Taiwancrapemyrtle: 'Subcostate_Crape_Myrtle_scan',
  TaiwanFlamegold: 'Taiwan_Golden_Rain_Tree_scan',
  SpinyBamboo: 'Thorny_Bamboo_scan'
}

// 建立去背（藍幕／綠幕）影片用的 ShaderMaterial。
// 以像素與 keyColor 的色差當作 alpha：差異越大越不透明，越接近 keyColor 越透明，
// 並用 despill 壓低邊緣殘留的色偏（去背常見的綠/藍色暈邊）。
function createChromaKeyMaterial(THREE, texture, keyColorHex, options = {}) {
  const similarity = options.similarity ?? 0.4
  const smoothness = options.smoothness ?? 0.1
  // 大檔案影片在手機上解碼／緩衝跟不上時，<video> 當下這一幀可能還沒有真正畫面，
  // GPU 貼圖會暫時是全黑（0,0,0）——這種黑跟藍/綠幕的色距很遠，會被誤判成「前景」而不透明，
  // 畫面上就會看到一塊黑影。因為正常拍攝的去背影片本來就不該出現純黑，這裡加一道安全網：
  // 亮度低於這個門檻的一律視為「還沒真正畫面」，強制當透明處理，不會誤傷真正較暗的前景顏色
  // （例如草的陰影，測試過大概都還有 3-4% 左右的亮度，明顯高於這個門檻）。
  const blackThreshold = options.blackThreshold ?? 0.03

  return new THREE.ShaderMaterial({
    uniforms: {
      map: { value: texture },
      keyColor: { value: new THREE.Color(keyColorHex) },
      similarity: { value: similarity },
      smoothness: { value: smoothness },
      blackThreshold: { value: blackThreshold }
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform sampler2D map;
      uniform vec3 keyColor;
      uniform float similarity;
      uniform float smoothness;
      uniform float blackThreshold;
      varying vec2 vUv;

      void main() {
        // 平面用 DoubleSide，兩面都看得到，但背面預設不會重新繞排 UV，
        // 直接看背面時畫面會呈鏡像。用 gl_FrontFacing 偵測目前是哪一面，
        // 背面時把 U 座標左右翻回來，確保不管相機從哪一側看畫面都不會被鏡像。
        vec2 uv = gl_FrontFacing ? vUv : vec2(1.0 - vUv.x, vUv.y);
        vec4 texColor = texture2D(map, uv);
        vec3 color = texColor.rgb;

        float d = distance(color, keyColor);
        float alpha = smoothstep(similarity, similarity + smoothness, d);

        // 貼圖還沒真正畫面資料時的黑幀安全網，見上方註解
        float brightness = max(color.r, max(color.g, color.b));
        if (brightness < blackThreshold) alpha = 0.0;

        if (alpha < 0.02) discard;

        // despill：找出去背色中最主導的色版，若畫面同一色版明顯偏高，代表邊緣殘留色暈，壓平到與其他色版同高
        vec3 despilled = color;
        float keyMax = max(keyColor.r, max(keyColor.g, keyColor.b));
        if (keyMax == keyColor.g) {
          despilled.g = min(despilled.g, max(despilled.r, despilled.b));
        } else if (keyMax == keyColor.b) {
          despilled.b = min(despilled.b, max(despilled.r, despilled.g));
        } else {
          despilled.r = min(despilled.r, max(despilled.g, despilled.b));
        }
        // 用 alpha 決定 despill 套用的程度，不要無條件全量套用——
        // similarity 調低後，原本會被直接 discard 掉的背景藍會落進「alpha 很低但沒低到discard」
        // 的過渡帶，這些像素本來就還很接近純藍(藍色分量遠高於另外兩色)，硬把藍色壓到跟另外兩色
        // 一樣高，三個色版就都趨近全暗，畫面上看起來是一大片黑——依 alpha 由淺入深套用就不會這樣
        color = mix(color, despilled, alpha);

        gl_FragColor = vec4(color, alpha);
      }
    `,
    side: THREE.DoubleSide,
    transparent: true,
    depthWrite: false,
    toneMapped: false
  })
}

// 建立一個「假影片元素」，實際上是預先載入一整組帶 alpha 的 PNG 序列圖，
// 逐張畫到 canvas 上模擬播放。用來取代去背——這種細碎枝葉的素材，色距去背再怎麼調
// 都會在邊緣留下藍色殘影或吃掉細節，改用算圖軟體直接輸出的真 alpha 序列圖才能做到
// 乾淨無殘影。刻意把介面設計成跟 <video> 元素相容（play/pause/addEventListener/
// readyState/videoWidth/videoHeight/loop/src），這樣 playChromaVideoLayer 裡
// 原本針對 <video> 寫的邏輯（等待首幀、ended 事件、清理）幾乎不用改就能直接沿用。
function createFrameSequenceElement(THREE, frameSeq, resolveUrl) {
  const { urlPattern, frameCount, fps = 30, pad = 3 } = frameSeq
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const texture = new THREE.CanvasTexture(canvas)
  const images = new Array(frameCount)
  const listeners = { loadeddata: [], ended: [] }

  let currentIndex = 0
  let playing = false
  let lastTick = 0
  let rafId = null
  const frameInterval = 1000 / fps

  const fake = {
    readyState: 0,
    videoWidth: 0,
    videoHeight: 0,
    loop: true,
    texture,
    addEventListener(type, cb, opts) {
      if (!listeners[type]) return
      const wrapped = (opts && opts.once) ? (...args) => { cb(...args); off(type, wrapped) } : cb
      listeners[type].push(wrapped)
    },
    play() {
      playing = true
      lastTick = 0
      rafId = requestAnimationFrame(tick)
      return Promise.resolve()
    },
    pause() {
      playing = false
      if (rafId) cancelAnimationFrame(rafId)
    },
    dispose() {
      playing = false
      if (rafId) cancelAnimationFrame(rafId)
      texture.dispose()
    }
  }
  Object.defineProperty(fake, 'src', { set() {} }) // 相容既有清理程式碼呼叫 videoEl.src = ''

  function off(type, fn) {
    const idx = listeners[type].indexOf(fn)
    if (idx !== -1) listeners[type].splice(idx, 1)
  }
  function emit(type) {
    listeners[type].slice().forEach(fn => fn())
  }
  function drawFrame(i) {
    const img = images[i]
    if (!img || !img.complete || !img.naturalWidth) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    texture.needsUpdate = true
  }
  function tick(ts) {
    if (!playing) return
    if (!lastTick) lastTick = ts
    if (ts - lastTick >= frameInterval) {
      lastTick = ts
      drawFrame(currentIndex)
      currentIndex++
      if (currentIndex >= frameCount) {
        if (!fake.loop) {
          playing = false
          emit('ended')
          return
        }
        currentIndex = 0
      }
    }
    rafId = requestAnimationFrame(tick)
  }

  for (let i = 0; i < frameCount; i++) {
    const img = new Image()
    img.onload = () => {
      if (canvas.width === 0) {
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        fake.videoWidth = img.naturalWidth
        fake.videoHeight = img.naturalHeight
      }
      if (i === 0) {
        fake.readyState = 2
        drawFrame(0)
        emit('loadeddata')
      }
    }
    img.onerror = () => console.error(`[FrameSequence] 影格載入失敗: ${img.src}`)
    img.src = resolveUrl(urlPattern.replace('{n}', String(i + 1).padStart(pad, '0')))
    images[i] = img
  }

  return fake
}

export default {
  name: 'ARpage',
  components: {
    TopNavigation,
    InfoPage
  },
  data() {
    return {
      devMode: false,
      devPanelOpen: true,
      devCurrentScale: 5,
      devScaleOverrides: {},
      devCurrentRotation: 0,
      devRotationOverrides: {},
      devCurrentOffset: { x: 0, y: 0, z: 0 },
      devOffsetOverrides: {},
      devCurrentVideoOffset: { x: 0, y: 0, z: 0 },
      devVideoOffsetOverrides: {},
      devExportText: '',
      devSavedMsg: '',
      _currentModelEl: null,
      _currentDistanceScale: 1,
      isShowIndexBtn: false, // 等待 XRExtras 載入後顯示
      isShowMapBtn: false,   // 等待 XRExtras 載入後顯示
      isShowDataBtn: false,  // 等待 XRExtras 載入後顯示
      isShowData: false,     // 暫時顯示 info 頁面
      isShowFinalPage: false, // 暫時顯示結束頁面
      isShowHint: false, // 顯示提示訊息
      isCanTap: false, // 顯示 3D 模型
      isAFrameLoaded: false, // A-Frame 是否已載入
      isModelLoading: false, // 模型正在載入中
      isLoading: false, // 顯示載入動畫
      isWasmReady: false,       // WebAssembly 模組是否已就緒
      wasmModuleUrl: null,      // 目前載入的 WASM 模組 URL
      _modelBlobUrl: null,      // 目前模型的 Blob URL（需手動 revoke）
      _preloadedBlobUrl: null,  // 預先載入的模型 Blob URL（供點擊時直接使用）
      selectedCircleId: null, // 從地圖選擇的點位 ID
      loadedScripts: [], // 追蹤已載入的 script 元素
      indexLink: window.location.protocol + '//' + window.location.host + getBasePath(),
      hintManager: {
        VitexNigra: {
          hintURL: require('@/assets/images/ar_hint_VitexNigra.png'),
          curserClass: 'VitexNigraCursor',
          hintIndex: 1,
        },
        Mango: {
          hintURL: require('@/assets/images/ar_hint_Mango.png'),
          curserClass: 'MangoCursor',
          hintIndex: 2,
        },
        PaperMulberry: {
          hintURL: require('@/assets/images/ar_hint_PaperMulberry.png'),
          curserClass: 'PaperMulberryCursor',
          hintIndex: 3,
        },
        SpinyBamboo: {
          hintURL: require('@/assets/images/ar_hint_SpinyBamboo.png'),
          curserClass: 'SpinyBambooCursor',
          hintIndex: 4,
        },
        Chinaberry: {
          hintURL: require('@/assets/images/ar_hint_Chinaberry.png'),
          curserClass: 'ChinaberryCursor',
          hintIndex: 5,
        },
        Areca: {
          hintURL: require('@/assets/images/ar_hint_Areca.png'),
          curserClass: 'ArecaCursor',
          hintIndex: 6,
        },
        Parasolleaftree: {
          hintURL: require('@/assets/images/ar_hint_Parasolleaftree.png'),
          curserClass: 'ParasolleaftreeCursor',
          hintIndex: 7,
        },
        TaiwanFlamegold: {
          hintURL: require('@/assets/images/ar_hint_TaiwanFlamegold.png'),
          curserClass: 'TaiwanFlamegoldCursor',
          hintIndex: 8,
        },
        Barringtoniaasiatica: {
          hintURL: require('@/assets/images/ar_hint_Barringtoniaasiatica.png'),
          curserClass: 'BarringtoniaasiaticaCursor',
          hintIndex: 9,
        },
        Taiwancrapemyrtle: {
          hintURL: require('@/assets/images/ar_hint_Taiwancrapemyrtle.png'),
          curserClass: 'TaiwancrapemyrtleCursor',
          hintIndex: 10,
        },
        Soapberry: {
          hintURL: require('@/assets/images/ar_hint_Soapberry.png'),
          curserClass: 'SoapberryCursor',
          hintIndex: 11,
        },
        LargeLeavedNanmu: {
          hintURL: require('@/assets/images/ar_hint_LargeLeavedNanmu.png'),
          curserClass: 'LargeLeavedNanmuCursor',
          hintIndex: 12,
        },
        Erythrina: {
          hintURL: require('@/assets/images/ar_hint_Erythrina.png'),
          curserClass: 'ErythrinaCursor',
          hintIndex: 13,
        },
        Imperata: {
          hintURL: require('@/assets/images/ar_hint_Imperata.png'),
          curserClass: 'ImperataCursor',
          hintIndex: 14,
        },
        Ficus: {
          hintURL: require('@/assets/images/ar_hint_Ficus.png'),
          curserClass: 'FicusCursor',
          hintIndex: 15,
        },
      },
      spotDataManager: {}, // 將從外部 JSON 配置文件載入
      languageTexts: {
        zh: {
          finishTitle: '動畫已播放完畢！',
          finishSubtitle: '',
          rewatchButton: '重新觀看',
          nextButton: '下一步',
          hintText: '請將場景對準虛線，再點下發光處！',
          loadingText: '載入中...',
          hint1Text: '請將虛線框對準黃荊旁石頭<br>並點擊手指圖示處',
          hint2Text: '請將虛線框對準芒果<br>並點擊手指圖示處',
          hint3Text: '請將虛線框對準構樹<br>並點擊手指圖示處',
          hint4Text: '請將虛線框對準防空洞<br>並點擊手指圖示處',
          hint5Text: '請將虛線框對準苦楝<br>並點擊手指圖示處',
          hint6Text: '請將虛線框對準檳榔旁石頭<br>並點擊手指圖示處',
          hint7Text: '請將虛線框對準血桐<br>並點擊手指圖示處',
          hint8Text: '請將虛線框對準台灣欒樹<br>並點擊手指圖示處',
          hint9Text: '請將虛線框對準棋盤腳<br>並點擊手指圖示處',
          hint10Text: '請將虛線框對準九芎<br>並點擊手指圖示處',
          hint11Text: '請將虛線框對準無患子<br>並點擊手指圖示處',
          hint12Text: '請將虛線框對準大葉楠<br>並點擊手指圖示處',
          hint13Text: '請將虛線框對準立牌<br>並點擊手指圖示處',
          hint14Text: '請將虛線框對準史前家屋<br>並點擊手指圖示處',
          hint15Text: '請將虛線框對準雀榕<br>並點擊手指圖示處',
        },
        en: {
          finishTitle: 'Animation Completed!',
          finishSubtitle: '',
          rewatchButton: 'Rewatch',
          nextButton: 'Next',
          hintText: 'Please align the scene with the dashed line, then tap the glowing area!',
          loadingText: 'Loading...',
          hint1Text: 'Please align the dashed box with the base of the Vitex Negundo<br>and tap the hand icon',
          hint2Text: 'Please align the dashed box with the base of the Mango tree<br>and tap the hand icon',
          hint3Text: 'Please align the dashed box with the base of the Paper Mulberry<br>and tap the hand icon',
          hint4Text: 'Please align the dashed box with the base of the air raid shelter<br>and tap the hand icon',
          hint5Text: 'Please align the dashed box with the base of the Chinaberry tree<br>and tap the hand icon',
          hint6Text: 'Please align the dashed box with the base of the Areca Palm<br>and tap the hand icon',
          hint7Text: 'Please align the dashed box with the base of the Parasol Leaf Tree<br>and tap the hand icon',
          hint8Text: 'Please align the dashed box with the base of the Taiwan Flamegold<br>and tap the hand icon',
          hint9Text: 'Please align the dashed box with the base of the Barringtonia<br>and tap the hand icon',
          hint10Text: 'Please align the dashed box with the base of the Taiwan Crape Myrtle<br>and tap the hand icon',
          hint11Text: 'Please align the dashed box with the base of the Soapberry tree<br>and tap the hand icon',
          hint12Text: 'Please align the dashed box with the base of the Large-Leaved Nanmu<br>and tap the hand icon',
          hint13Text: 'Please align the dashed box with the base of the Erythrina<br>and tap the hand icon',
          hint14Text: 'Please align the dashed box with the base of the Prehistoric House<br>and tap the hand icon',
          hint15Text: 'Please align the dashed box with the base of the Ficus<br>and tap the hand icon',
        },
        bn: {
          finishTitle: 'アニメーションが完了しました！',
          finishSubtitle: '',
          rewatchButton: 'もう一度見る',
          nextButton: '次へ',
          hintText: 'シーンを点線に合わせて、光る部分をタップしてください！',
          loadingText: '読み込み中...',
          hint1Text: '点線の枠を黃荊の根元に合わせて<br>手のアイコンをタップしてください',
          hint2Text: '点線の枠をマンゴーの根元に合わせて<br>手のアイコンをタップしてください',
          hint3Text: '点線の枠をコウゾの根元に合わせて<br>手のアイコンをタップしてください',
          hint4Text: '点線の枠を防空壕の基部に合わせて<br>手のアイコンをタップしてください',
          hint5Text: '点線の枠をセンダンの根元に合わせて<br>手のアイコンをタップしてください',
          hint6Text: '点線の枠をビンロウの根元に合わせて<br>手のアイコンをタップしてください',
          hint7Text: '点線の枠をマカランガの根元に合わせて<br>手のアイコンをタップしてください',
          hint8Text: '点線の枠をタイワンモクゲンジの根元に合わせて<br>手のアイコンをタップしてください',
          hint9Text: '点線の枠をサガリバナの根元に合わせて<br>手のアイコンをタップしてください',
          hint10Text: '点線の枠をシマサルスベリの根元に合わせて<br>手のアイコンをタップしてください',
          hint11Text: '点線の枠をムクロジの根元に合わせて<br>手のアイコンをタップしてください',
          hint12Text: '点線の枠をオオバタブの根元に合わせて<br>手のアイコンをタップしてください',
          hint13Text: '点線の枠をデイゴの根元に合わせて<br>手のアイコンをタップしてください',
          hint14Text: '点線の枠を先史時代の家屋の基部に合わせて<br>手のアイコンをタップしてください',
          hint15Text: '点線の枠をガジュマルの根元に合わせて<br>手のアイコンをタップしてください',
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return Cookies.get('lang') || 'zh'
    },
    currentWayspotData() {
      const point = this.$route.query.point
      const dbKey = pointKeyMap[point]
      if (!dbKey) return null
      return wayspotDataManager.getWayspotData(dbKey, this.currentLanguage)
    },
    currentSpotData() {
      return this.$route.query.point || 'SpinyBamboo'
    },
    currentModelUrl() {
      const spotData = this.spotDataManager[this.currentSpotData]
      if (spotData && spotData[this.currentLanguage]) {
        return withBase(spotData[this.currentLanguage].modelUrl)
      }
      return withBase('/assets/models/AR1_noon_moonstone.glb')
    },
    currentAudioUrl() {
      const spotData = this.spotDataManager[this.currentSpotData]
      if (spotData && spotData[this.currentLanguage]) {
        return withBase(spotData[this.currentLanguage].audioUrl)
      }
      // 預設返回刺竹音效
      return withBase('/assets/audio/AR1_tw.mp3')
    },
  },
  mounted() {
    if (DEV_MODE) {
      this.devMode = true
    }

    // iOS Safari bfcache 修復：頁面從快取還原時強制重新載入，避免 XR session 衝突
    this._onPageShow = (event) => {
      if (event.persisted) {
        console.log('Page restored from bfcache, forcing reload...')
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', this._onPageShow)

    // 讀取從地圖頁傳來的 circleId
    this.selectedCircleId = this.$route.query.point
    console.log('選擇的點位:', this.selectedCircleId)

    // 載入外部配置文件，完成後立即預載模型（與原本 a-asset-item 預載時機相同）
    this.loadModelConfig().then(() => {
      console.log('Model config loaded successfully')
      if (this.devMode) {
        const cfg = this.spotDataManager[this.currentSpotData]
        const scaleOverride = this.devScaleOverrides[this.currentSpotData]
        this.devCurrentScale = scaleOverride !== undefined
          ? Number(scaleOverride)
          : Number((cfg?.scale || '5 5 5').split(' ')[0])
        const rotOverride = this.devRotationOverrides[this.currentSpotData]
        this.devCurrentRotation = rotOverride !== undefined
          ? Number(rotOverride)
          : Number(cfg?.rotationOffset || 0)
      }
      return this.preloadModel()
    }).then(() => {
      console.log('Model preload completed')
    }).catch(error => {
      console.error('Failed to load model config or preload model:', error)
    })

    // 初始化 WebAssembly 模組
    this.initWasmModule()

    // 載入 8thWall 和 A-Frame 所需的 scripts
    this.load8thWallScripts().then(() => {
      console.log('8thWall scripts loaded successfully')
      // 設置為已載入
      this.isAFrameLoaded = true
      // 等待 Vue 渲染完成後初始化組件
      this.$nextTick(() => {
        this.initTapPlaceComponent()
      })
    }).catch(error => {
      console.error('Failed to load 8thWall scripts:', error)
      alert('AR 場景載入失敗，請重新整理頁面')
    })

  },
  beforeUnmount() {
    // 移除 bfcache 監聽器
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow)
    }

    // 清除待觸發／播放中的插播影片
    if (this._spotVideoTimeout) {
      clearTimeout(this._spotVideoTimeout)
      this._spotVideoTimeout = null
    }
    this.removeSpotVideo()
    this.removeAllChromaVideoLayers()
    this.clearTextureChanges()

    // 執行 AR 場景清理（與 onBackToMap 共用同一套邏輯）
    this.cleanupARScene()

    // 釋放 WASM 模組快取
    if (this.wasmModuleUrl) {
      clearWasmCache(this.wasmModuleUrl)
    }

    // 釋放預載 Blob URL（cleanupARScene 只處理使用中的，預載的在這裡釋放）
    if (this._preloadedBlobUrl) {
      URL.revokeObjectURL(this._preloadedBlobUrl)
      this._preloadedBlobUrl = null
    }
  },
  methods: {
    // 模型播放結束，前往 QA 頁
    nextPage() {
      window.location.href = this.indexLink + 'qa?point=' + this.selectedCircleId
    },
    // 初始化 WebAssembly 模組
    async initWasmModule() {
      if (!isWasmSupported()) {
        console.warn('[WASM] 此裝置不支援 WebAssembly，相關功能將被停用')
        return
      }

      console.log('[WASM] WebAssembly 環境支援確認完成（模組待掛載）')
      this.isWasmReady = true
    },

    // 預先載入模型並快取為 Blob URL（供點擊時零延遲使用）
    async preloadModel() {
      try {
        const buffer = await fetchAndProcessModel(
          this.currentModelUrl,
          this.isWasmReady ? this.wasmModuleUrl : null
        )
        // 釋放舊的預載 Blob URL
        if (this._preloadedBlobUrl) {
          URL.revokeObjectURL(this._preloadedBlobUrl)
        }
        const blob = new Blob([buffer], { type: 'model/gltf-binary' })
        this._preloadedBlobUrl = URL.createObjectURL(blob)
        console.log('[WASM] 模型預載完成，Blob URL 已建立')
      } catch (error) {
        console.warn('[WASM] 模型預載失敗，將於點擊時重新嘗試：', error)
      }
    },

    // 載入外部模型配置
    async loadModelConfig() {
      try {
        const response = await fetch(withBase('/config/ar-models-config.json'))
        if (!response.ok) {
          throw new Error('Failed to fetch model config')
        }
        const config = await response.json()
        delete config.gpsCalibration // 非景點資料，MAPpage 才會用到
        this.spotDataManager = config
        console.log('Loaded model config:', this.spotDataManager)
      } catch (error) {
        console.error('Error loading model config:', error)
        this.spotDataManager = {}
      }
    },
    onBackToIndex() {
      window.location.href = this.indexLink
    },
    onBackToMap() {
      this.cleanupARScene()
      // 留一點時間讓它先釋放乾淨再跳轉。
      setTimeout(() => {
        window.location.href = './mapPage'
      }, 300)
    },

    cleanupARScene() {
      // 停止所有動態 entity 的音效與動畫，然後移除
      if (this.$refs.arScene) {
        const scene = this.$refs.arScene
        const dynamicEntities = scene.querySelectorAll('a-entity[gltf-model]')
        dynamicEntities.forEach(entity => {
          if (entity.components?.sound) {
            try { entity.components.sound.stopSound() } catch (e) { /* ignore */ }
          }
          if (entity.components?.['animation-mixer']) {
            entity.removeAttribute('animation-mixer')
          }
          if (entity.parentNode) entity.parentNode.removeChild(entity)
        })

        // 暫停 A-Frame 場景渲染
        if (scene.hasLoaded) {
          try { scene.pause() } catch (e) { /* ignore */ }
        }
      }

      // 嘗試停止 XR session，避免 WebGL context 殘留
      if (typeof XR !== 'undefined') {
        try { XR.stop() } catch (e) { /* ignore */ }
      }

      this._currentModelEl = null
      this._currentDistanceScale = 1

      // 釋放使用中的 Blob URL（預載的留給 beforeUnmount 處理）
      if (this._modelBlobUrl) {
        URL.revokeObjectURL(this._modelBlobUrl)
        this._modelBlobUrl = null
      }
    },
    // ── 開發者模式：調整 / 儲存 / 匯出 scale ────────────────────────
    devAdjustScale(delta) {
      this.devCurrentScale = Math.max(0.1, parseFloat((this.devCurrentScale + delta).toFixed(1)))
      this.devExportText = ''
      const s = this.devCurrentScale * (this._currentDistanceScale || 1)
      if (this._currentModelEl) {
        this._currentModelEl.setAttribute('scale', `${s} ${s} ${s}`)
      }
    },
    devAdjustRotation(delta) {
      this.devCurrentRotation = this.devCurrentRotation + delta
      this.devExportText = ''
      if (this._currentModelEl) {
        const curY = this._currentModelEl.object3D.rotation.y * (180 / Math.PI)
        this._currentModelEl.setAttribute('rotation', `0 ${curY + delta} 0`)
      }
    },
    // 在模型的父子關係下建立一個播放影片的平面，讓影片位置/朝向永遠跟著模型（不影響 AR 模型動畫/音效）
    playSpotVideo(spotData) {
      const parentEl = this._currentModelEl
      if (!parentEl) return

      const videoEl = document.createElement('video')
      videoEl.setAttribute('playsinline', '')
      videoEl.setAttribute('webkit-playsinline', '')
      videoEl.muted = true
      videoEl.playsInline = true
      videoEl.loop = false
      videoEl.preload = 'auto'
      videoEl.addEventListener('error', () => {
        console.error('[SpotVideo] 影片載入失敗:', videoEl.error)
      })
      videoEl.src = withBase(spotData.videoUrl)
      videoEl.load()

      const THREE = AFRAME.THREE
      const texture = new THREE.VideoTexture(videoEl)
      const radius = (spotData.videoWidth || 1) / 2
      const geometry = new THREE.CircleGeometry(radius, 48)
      const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false })
      const mesh = new THREE.Mesh(geometry, material)

      // 影片為模型的子節點，位置以模型的局部座標系為準，會自動隨模型的位置/朝向移動
      const videoEntity = document.createElement('a-entity')
      videoEntity.setAttribute('position', { ...this.devCurrentVideoOffset })
      videoEntity.object3D.add(mesh)
      parentEl.appendChild(videoEntity)

      this._spotVideoEl = videoEl
      this._spotVideoEntity = videoEntity

      // 子節點會繼承模型的縮放，反向抵銷以維持 videoWidth/videoHeight 設定的實際大小
      const modelScaleFactor = (this.devCurrentScale || 1) * (this._currentDistanceScale || 1)
      const targetScale = modelScaleFactor ? 1 / modelScaleFactor : 1

      // 出現：彈性放大，動畫結束後才開始播放影片
      videoEntity.addEventListener('animationcomplete__appear', () => {
        videoEl.play().catch(err => {
          console.warn('[SpotVideo] 帶聲音播放被瀏覽器擋下，改為靜音播放:', err)
          videoEl.muted = true
          videoEl.play().catch(err2 => console.error('[SpotVideo] 靜音播放仍失敗:', err2))
        })
      }, { once: true })
      videoEntity.setAttribute('scale', '0 0 0')
      videoEntity.setAttribute('animation__appear', {
        property: 'scale',
        from: '0 0 0',
        to: `${targetScale} ${targetScale} ${targetScale}`,
        dur: 800,
        easing: 'easeOutElastic'
      })
      videoEl.addEventListener('ended', () => this.hideSpotVideo())
    },
    // 退場：彈性縮小後才真正移除
    hideSpotVideo() {
      const entity = this._spotVideoEntity
      if (!entity) return
      if (this._spotVideoEl) this._spotVideoEl.pause()

      entity.addEventListener('animationcomplete__exit', () => this.removeSpotVideo(), { once: true })
      entity.setAttribute('animation__exit', {
        property: 'scale',
        to: '0 0 0',
        dur: 800,
        easing: 'easeInBack'
      })
    },
    removeSpotVideo() {
      if (this._spotVideoEl) {
        this._spotVideoEl.pause()
        this._spotVideoEl.src = ''
        this._spotVideoEl = null
      }
      if (this._spotVideoEntity) {
        if (this._spotVideoEntity.parentNode) this._spotVideoEntity.parentNode.removeChild(this._spotVideoEntity)
        this._spotVideoEntity = null
      }
    },
    // 依 config 的 textureChanges 清單，在模型動畫播放到指定秒數時，
    // 把符合名稱的貼圖換成新圖片（名稱比對邏輯跟 console 印出的 mesh list 一致：
    // 優先用 texture.name，沒有的話退回用檔名），並沿用原本的 wrap/repeat/偏移等設定
    setupTextureChanges(model, changes) {
      this._textureChangeTimeouts = this._textureChangeTimeouts || []
      if (!model) return
      const THREE = AFRAME.THREE
      const loader = new THREE.TextureLoader()

      changes.forEach(change => {
        const timeoutId = setTimeout(() => {
          let found = false
          model.traverse((child) => {
            if (!child.isMesh) return
            const materials = Array.isArray(child.material) ? child.material : [child.material]
            materials.forEach((mat) => {
              Object.keys(mat).forEach((key) => {
                const tex = mat[key]
                if (!tex || !tex.isTexture) return
                const texId = tex.name || tex.image?.src?.split('/').pop() || '(未命名)'
                if (texId !== change.textureName) return

                found = true
                loader.load(withBase(change.url), (newTex) => {
                  // 沿用原本貼圖的重複／偏移／座標系設定，避免換圖後版面跑掉
                  newTex.wrapS = tex.wrapS
                  newTex.wrapT = tex.wrapT
                  newTex.repeat.copy(tex.repeat)
                  newTex.offset.copy(tex.offset)
                  newTex.rotation = tex.rotation
                  newTex.center.copy(tex.center)
                  newTex.flipY = tex.flipY
                  if (tex.colorSpace) newTex.colorSpace = tex.colorSpace
                  if (tex.encoding !== undefined) newTex.encoding = tex.encoding
                  newTex.name = change.textureName

                  mat[key] = newTex
                  mat.needsUpdate = true
                  tex.dispose()
                })
              })
            })
          })
          if (!found) {
            console.warn(`[TextureChange] 找不到名稱為 "${change.textureName}" 的貼圖，請對照 console 印出的 mesh list 確認名稱`)
          }
        }, (change.time || 0) * 1000)
        this._textureChangeTimeouts.push(timeoutId)
      })
    },
    // 清除所有待觸發的換貼圖排程（模型動畫結束／離開頁面時呼叫）
    clearTextureChanges() {
      if (this._textureChangeTimeouts) {
        this._textureChangeTimeouts.forEach(id => clearTimeout(id))
        this._textureChangeTimeouts = []
      }
    },
    // 依 config 的 chromaVideos 清單，各自排程在模型動畫第幾秒出現
    setupChromaVideos(layers) {
      this._chromaVideoTimeouts = this._chromaVideoTimeouts || []
      this._chromaVideoLayers = this._chromaVideoLayers || []
      layers.forEach(layer => {
        const startAt = (layer.startAt || 0) * 1000
        const timeoutId = setTimeout(() => this.playChromaVideoLayer(layer), startAt)
        this._chromaVideoTimeouts.push(timeoutId)
      })
    },
    // 建立單一去背影片圖層，做為模型的子節點（跟著模型位置/朝向走）
    playChromaVideoLayer(layer) {
      const parentEl = this._currentModelEl
      if (!parentEl) return

      const THREE = AFRAME.THREE
      // frameSequence 圖層：用預先算好帶真 alpha 的 PNG 序列圖取代影片，跳過去背整套邏輯——
      // 細碎枝葉這種素材色距去背天生會有殘影，真 alpha 才能乾淨。介面刻意跟 <video> 相容，
      // 下面幾乎所有邏輯（等首幀、position/motion/pivotY、playOnce ended、清理）都能直接沿用。
      const isFrameSeq = !!layer.frameSequence
      const videoEl = isFrameSeq
        ? createFrameSequenceElement(THREE, layer.frameSequence, withBase)
        : document.createElement('video')

      if (!isFrameSeq) {
        videoEl.setAttribute('playsinline', '')
        videoEl.setAttribute('webkit-playsinline', '')
        videoEl.muted = true
        videoEl.playsInline = true
        videoEl.preload = 'auto'
        videoEl.addEventListener('error', () => {
          console.error(`[ChromaVideo:${layer.id}] 影片載入失敗:`, videoEl.error)
        })
        videoEl.src = withBase(layer.url)
        videoEl.load()
      }
      // playOnce 的圖層（例如播完就該消失的背景影片）只播一次，
      // 其餘圖層（持續播放的背景、行人走路循環）維持原本的無限 loop
      videoEl.loop = !layer.playOnce

      const texture = isFrameSeq ? videoEl.texture : new THREE.VideoTexture(videoEl)
      // 去背開關：layer.chromaKey === false 或本身就是帶真 alpha 的 frameSequence 時，
      // 直接吃來源本身的 alpha，不用色距去背；預設仍是 true 沿用既有的藍/綠幕去背行為
      const material = (layer.chromaKey === false || isFrameSeq)
        ? new THREE.MeshBasicMaterial({ map: texture, transparent: true, side: THREE.DoubleSide, toneMapped: false })
        : createChromaKeyMaterial(THREE, texture, layer.keyColor, layer.chroma)

      const [px, py, pz] = (layer.position || '0 0 0').split(' ').map(Number)

      // 外層 anchor：只負責位置／位移動畫／縮放，座標軸跟模型本身的 local 軸一致。
      // 這層絕對不能加旋轉 —— 加了的話位移動畫的方向會被連帶轉掉
      // （這正是「轉向對了但位置/移動跟著跑掉」那個回報的根因）。
      const anchorEntity = document.createElement('a-entity')
      anchorEntity.setAttribute('position', { x: px || 0, y: py || 0, z: pz || 0 })
      parentEl.appendChild(anchorEntity)

      // 內層 facing：只負責固定的 90 度朝向校正，跟 anchor 分開一層，
      // 校正旋轉的軸不會因此汙染到 anchor 的位移座標軸。
      const facingEntity = document.createElement('a-entity')
      facingEntity.setAttribute('rotation', '0 90 0')
      anchorEntity.appendChild(facingEntity)

      const width = layer.width || 2
      // 要等到「至少解出第一幀畫面」才能建立 mesh，不能只等 metadata（readyState 1）—
      // metadata 只代表知道長寬比，此時畫面還沒有真正的影格資料，VideoTexture 貼圖
      // 會先呈現全黑，黑色離去背色（藍/綠）很遠，去背 shader 會判定成「不透明」，
      // 顯示出來就是一整片黑影，要等 readyState 2（HAVE_CURRENT_DATA）才有真正畫面。
      const buildMesh = () => {
        const aspect = (videoEl.videoWidth && videoEl.videoHeight)
          ? (videoEl.videoWidth / videoEl.videoHeight)
          : (16 / 9)
        const height = width / aspect
        const geometry = new THREE.PlaneGeometry(width, height)
        const mesh = new THREE.Mesh(geometry, material)
        if (layer.mirror) mesh.scale.x = -1
        // PlaneGeometry 預設以幾何中心為支點，等比放大時上下左右會同時展開。
        // 對「站在地面上」的背景（樹、行人）來說，這會讓調大 scale 時視覺上的地面線跟著往下沉，
        // 感覺就像位置被拖著移動。用 pivotY 把支點挪到底邊，放大時改成只往上長，地面線固定不動。
        if (layer.pivotY === 'bottom') mesh.position.y = height / 2
        else if (layer.pivotY === 'top') mesh.position.y = -height / 2
        facingEntity.object3D.add(mesh)
      }
      if (videoEl.readyState >= 2) {
        buildMesh()
      } else {
        videoEl.addEventListener('loadeddata', buildMesh, { once: true })
      }

      // 初始大小：config 的 scale 倍率（預設 1），設在 anchor 上。
      // anchor 是模型的子節點，這裡設的是「相對模型的倍率」，
      // 模型本身縮放改變時（distanceScale／dev 面板調整）會自動連動放大縮小，
      // 不需要另外反向抵銷 —— tree/walkman 本來就該跟著模型一起變大變小，
      // 不像 playSpotVideo 那支慶祝用彈窗影片需要維持固定的實際尺寸。
      const initialScale = layer.scale ?? 1
      anchorEntity.setAttribute('scale', `${initialScale} ${initialScale} ${initialScale}`)

      videoEl.play().catch(err => {
        console.warn(`[ChromaVideo:${layer.id}] 自動播放被擋下:`, err)
      })

      const record = { entity: anchorEntity, videoEl, timeoutId: null }
      this._chromaVideoLayers.push(record)

      // 三種圖層各自的存活方式：
      // 有 motion 的（行人）：位移＋縮小動畫播完即移除；
      // playOnce 的（例如播完就該消失的背景影片）：影片自己 ended 時縮小移除，見下方；
      // 都沒有的（持續播放的背景）：一直播放，直到模型動畫結束才統一清除
      if (layer.motion) {
        const duration = (layer.motion.duration || 10) * 1000
        const distance = layer.motion.distance ?? 2
        const dirSign = layer.motion.direction === 'rtl' ? -1 : 1
        // endScale 是相對 initialScale 的縮小比例（例如 0.4 = 縮到初始大小的 40%）
        const endScale = initialScale * (layer.motion.endScale ?? 0.4)

        // facing 固定做了 90 度朝向校正，校正後畫面本身的「寬度／行走方向」軸
        // 對應到 anchor 的 Z 軸，不是直覺以為的 X 軸（90 度旋轉會把 X/Z 互換）。
        // 如果實機看到方向不對或軸選錯了，改這裡的 motion.axis（'x' 或 'z'）即可，不用動程式碼。
        const motionAxis = layer.motion.axis || 'z'
        const from = { x: px || 0, y: py || 0, z: pz || 0 }
        const to = { ...from }
        to[motionAxis] = (from[motionAxis] || 0) + dirSign * distance

        anchorEntity.setAttribute('animation__walk', {
          property: 'position',
          from: `${from.x} ${from.y} ${from.z}`,
          to: `${to.x} ${to.y} ${to.z}`,
          dur: duration,
          easing: 'linear'
        })
        anchorEntity.setAttribute('animation__shrink', {
          property: 'scale',
          from: `${initialScale} ${initialScale} ${initialScale}`,
          to: `${endScale} ${endScale} ${endScale}`,
          dur: duration,
          easing: 'easeInQuad'
        })

        record.timeoutId = setTimeout(() => this.removeChromaVideoLayer(record), duration)
      }

      // playOnce 的圖層：不用固定計時器，直接聽影片本身的 ended 事件，
      // 播完當下才縮小消失，跟影片實際長度同步（不用手動填對的秒數，也不怕影片改長度後跟計時器對不上）
      if (layer.playOnce) {
        videoEl.addEventListener('ended', () => this.shrinkAndRemoveChromaVideoLayer(record), { once: true })
      }
    },
    // 彈性縮小後才真正移除（跟 hideSpotVideo 的退場手感一致）
    shrinkAndRemoveChromaVideoLayer(record) {
      if (!record.entity) return
      record.entity.addEventListener('animationcomplete__shrinkOut', () => this.removeChromaVideoLayer(record), { once: true })
      record.entity.setAttribute('animation__shrinkOut', {
        property: 'scale',
        to: '0 0 0',
        dur: 800,
        easing: 'easeInBack'
      })
    },
    removeChromaVideoLayer(record) {
      if (record.timeoutId) clearTimeout(record.timeoutId)
      if (record.videoEl) {
        record.videoEl.pause()
        record.videoEl.src = ''
        if (record.videoEl.dispose) record.videoEl.dispose() // frameSequence 圖層才有，釋放 canvas 貼圖
      }
      if (record.entity?.parentNode) record.entity.parentNode.removeChild(record.entity)
      const idx = this._chromaVideoLayers.indexOf(record)
      if (idx !== -1) this._chromaVideoLayers.splice(idx, 1)
    },
    // 清除所有待觸發的排程與目前播放中的去背影片圖層
    removeAllChromaVideoLayers() {
      if (this._chromaVideoTimeouts) {
        this._chromaVideoTimeouts.forEach(id => clearTimeout(id))
        this._chromaVideoTimeouts = []
      }
      if (this._chromaVideoLayers) {
        // slice() 複製陣列，避免 removeChromaVideoLayer 內部 splice 造成疊代跳號
        this._chromaVideoLayers.slice().forEach(record => this.removeChromaVideoLayer(record))
      }
    },
    devAdjustOffset(axis, delta) {
      this.devCurrentOffset[axis] = parseFloat((this.devCurrentOffset[axis] + delta).toFixed(2))
      this.devExportText = ''
      if (this._currentModelEl && this._baseModelPosition) {
        const base = this._baseModelPosition
        const off = this.devCurrentOffset
        this._currentModelEl.setAttribute('position', {
          x: base.x + off.x,
          y: base.y + off.y,
          z: base.z + off.z
        })
      }
    },
    devAdjustVideoOffset(axis, delta) {
      this.devCurrentVideoOffset[axis] = parseFloat((this.devCurrentVideoOffset[axis] + delta).toFixed(2))
      this.devExportText = ''
      if (this._spotVideoEntity) {
        this._spotVideoEntity.setAttribute('position', { ...this.devCurrentVideoOffset })
      }
    },
    // 開發者模式：略過等待秒數，立即在目前偏移位置播放插播影片以便調整位置
    devTestSpotVideo() {
      const spotData = this.spotDataManager[this.currentSpotData]
      if (!spotData?.videoUrl || !this._currentModelEl) return
      this.removeSpotVideo()
      this.playSpotVideo(spotData)
    },
    devSave() {
      this.devScaleOverrides[this.currentSpotData] = this.devCurrentScale
      this.devRotationOverrides[this.currentSpotData] = this.devCurrentRotation
      this.devOffsetOverrides[this.currentSpotData] = { ...this.devCurrentOffset }
      this.devVideoOffsetOverrides[this.currentSpotData] = { ...this.devCurrentVideoOffset }
      this.devSavedMsg = '✓ 已儲存'
      setTimeout(() => { this.devSavedMsg = '' }, 2000)
    },
    // 清除此景點所有已儲存的覆寫值，改回讀取 config 目前的預設值（並立即套用到畫面上）
    devClearOverrides() {
      const spotId = this.currentSpotData
      const spotData = this.spotDataManager[spotId]

      delete this.devScaleOverrides[spotId]
      delete this.devRotationOverrides[spotId]
      delete this.devOffsetOverrides[spotId]
      delete this.devVideoOffsetOverrides[spotId]

      // 重新從 config 讀出預設值並套用到目前已放置的模型／影片
      const baseScale = Number((spotData?.scale || '7 7 7').split(' ')[0])
      this.devCurrentScale = baseScale
      if (this._currentModelEl) {
        const s = baseScale * (this._currentDistanceScale || 1)
        this._currentModelEl.setAttribute('scale', `${s} ${s} ${s}`)
      }

      this.devCurrentRotation = spotData?.rotationOffset || 0
      if (this._currentModelEl) {
        this._currentModelEl.setAttribute('rotation', `0 ${this._baseAngleToCamera || 0} 0`)
      }

      const baseOffset = (spotData?.modelOffset || '0 0 0').split(' ').map(Number)
      this.devCurrentOffset = { x: baseOffset[0] || 0, y: baseOffset[1] || 0, z: baseOffset[2] || 0 }
      if (this._currentModelEl && this._baseModelPosition) {
        const base = this._baseModelPosition
        this._currentModelEl.setAttribute('position', {
          x: base.x + this.devCurrentOffset.x,
          y: base.y + this.devCurrentOffset.y,
          z: base.z + this.devCurrentOffset.z
        })
      }

      const baseVideoOffset = (spotData?.videoOffset || '1 0 0').split(' ').map(Number)
      this.devCurrentVideoOffset = { x: baseVideoOffset[0] || 0, y: baseVideoOffset[1] || 0, z: baseVideoOffset[2] || 0 }
      if (this._spotVideoEntity) {
        this._spotVideoEntity.setAttribute('position', { ...this.devCurrentVideoOffset })
      }

      this.devExportText = ''
      this.devSavedMsg = '✓ 已清除覆寫，套用 config 預設值'
      setTimeout(() => { this.devSavedMsg = '' }, 2000)
    },
    devExport() {
      // 產出完整 config JSON（所有模型，已套用累積的 scale 覆寫值）
      const output = {}
      Object.keys(this.spotDataManager).forEach(spotId => {
        const base = this.spotDataManager[spotId]
        const override = this.devScaleOverrides[spotId]
        const rotOverride = this.devRotationOverrides[spotId]
        const offOverride = this.devOffsetOverrides[spotId]
        const videoOffOverride = this.devVideoOffsetOverrides[spotId]
        output[spotId] = {
          ...base,
          scale: override !== undefined ? `${override} ${override} ${override}` : base.scale,
          rotationOffset: rotOverride !== undefined ? rotOverride : base.rotationOffset,
          modelOffset: offOverride ? `${offOverride.x} ${offOverride.y} ${offOverride.z}` : (base.modelOffset || '0 0 0'),
          ...(base.videoUrl ? {
            videoOffset: videoOffOverride ? `${videoOffOverride.x} ${videoOffOverride.y} ${videoOffOverride.z}` : (base.videoOffset || '1 0 0')
          } : {})
        }
      })
      this.devExportText = JSON.stringify(output, null, 2)
    },
    // ─────────────────────────────────────────────────────────────────
    // Google Analytics：景點 AR 動畫成功開始播放時回報一次自訂事件
    trackGAEvent() {
      const wayspotTitle = this.currentSpotData
      const eventName = GA_EVENT_NAME_MAP[wayspotTitle]

      if (!eventName) {
        console.log(`=== 未找到對應的 GA 事件: ${wayspotTitle} ===`)
        return
      }

      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, {
          event_category: '事件',
          event_label: wayspotTitle
        })
        console.log(`=== GA Event: ${eventName} - ${wayspotTitle} ===`)
      }
    },
    onOpenDataPage() {
      this.isShowData = true
      document.getElementById('btn_data').style.backgroundColor = '#fdd089';
    },
    closeInfoPage() {
      this.isShowData = false
      const btnData = document.getElementById('btn_data');
      if (btnData) {
        btnData.style.backgroundColor = 'rgba(153, 153, 153, 0.7)';
      }
    },
    reScanTarget() {
      // 清除待觸發／播放中的插播影片
      if (this._spotVideoTimeout) {
        clearTimeout(this._spotVideoTimeout)
        this._spotVideoTimeout = null
      }
      this.removeSpotVideo()
      this.removeAllChromaVideoLayers()
      this.clearTextureChanges()

      // 移除場景中的所有動態創建的模型
      if (this.$refs.arScene) {
        const scene = this.$refs.arScene
        const dynamicEntities = scene.querySelectorAll('a-entity[gltf-model]')
        dynamicEntities.forEach(entity => {
          // 停止音效
          if (entity.components.sound) {
            entity.components.sound.stopSound()
          }
          // 移除實體
          if (entity.parentNode) {
            entity.parentNode.removeChild(entity)
          }
        })
        console.log('Removed all dynamic models for replay')
      }

      // 釋放舊的 Blob URL
      if (this._modelBlobUrl) {
        URL.revokeObjectURL(this._modelBlobUrl)
        this._modelBlobUrl = null
      }
      this._currentModelEl = null
      this._currentDistanceScale = 1

      // 重置狀態，允許重新點擊
      this.isShowFinalPage = false
      this.isShowHint = true
      this.isCanTap = true
      this.isModelLoading = false
    },
    // 載入 8thWall 所需的 scripts
    load8thWallScripts() {
      return new Promise((resolve, reject) => {
        // 檢查 A-Frame 是否已經載入
        if (typeof window.AFRAME !== 'undefined') {
          console.log('A-Frame already loaded, skipping script loading')
          // 嘗試在 XR session 啟動前設定相機就緒監聽
          this.setupCameraReadyListener()
          resolve()
          return
        }

        // 檢查 DOM 中是否已有相關 scripts（防止重複添加）
        const existingScripts = document.querySelectorAll('script[src*="8thwall"], script[src*="aframe"]')
        if (existingScripts.length > 0) {
          console.log('Scripts already in DOM, waiting for AFRAME...')
          // 等待 AFRAME 可用
          const checkAFrame = setInterval(() => {
            if (typeof window.AFRAME !== 'undefined') {
              clearInterval(checkAFrame)
              resolve()
            }
          }, 100)
          return
        }

        console.log('Loading A-Frame scripts for the first time...')

        // 載入 A-Frame
        const aframeScript = document.createElement('script')
        aframeScript.src = withBase('/js/external/scripts/8frame-1.5.0.min.js')
        this.loadedScripts.push(aframeScript)

        aframeScript.onload = () => {
          const aframeExtraScript = document.createElement('script')
          aframeExtraScript.src = withBase('/js/external/xrextras/aframe-extras-6.1.1.min.js')
          this.loadedScripts.push(aframeExtraScript)

          aframeExtraScript.onload = () => {
            // A-Frame 載入完成後，載入 XR Extras
            const xrExtrasScript = document.createElement('script')
            xrExtrasScript.src = withBase('/js/external/xrextras/xrextras.js')
            this.loadedScripts.push(xrExtrasScript)

            xrExtrasScript.onload = () => {
              console.log('XR Extras loaded')
              // XR Extras 載入完成後，載入 Landing Page
              const landingPageScript = document.createElement('script')
              landingPageScript.src = withBase('/js/external/landing-page/landing-page.js')
              this.loadedScripts.push(landingPageScript)

              landingPageScript.onload = () => {
                console.log('Landing Page loaded')

                // Landing Page 載入完成後,載入 8thWall Web (使用本地檔案)
                const eighthWallScript = document.createElement('script')
                eighthWallScript.src = withBase('/js/external/xr/xr.js')
                eighthWallScript.crossOrigin = 'anonymous'
                eighthWallScript.async = true
                eighthWallScript.setAttribute('data-preload-chunks', 'face, slam')
                this.loadedScripts.push(eighthWallScript)

                eighthWallScript.onload = () => {
                  console.log('8thWall Web loaded')
                  // 在 a-scene 渲染（XR session 啟動）前設定相機就緒監聽
                  this.setupCameraReadyListener()
                  resolve()
                }

                eighthWallScript.onerror = () => reject(new Error('Failed to load 8thWall Web'))
                document.head.appendChild(eighthWallScript)
              }

              landingPageScript.onerror = () => reject(new Error('Failed to load Landing Page'))
              document.head.appendChild(landingPageScript)
            }

            xrExtrasScript.onerror = () => reject(new Error('Failed to load XR Extras'))
            document.head.appendChild(xrExtrasScript)
          }
          aframeExtraScript.onerror = () => reject(new Error('Failed to load XR Extras'))
          document.head.appendChild(aframeExtraScript)
        }

        aframeScript.onerror = () => reject(new Error('Failed to load A-Frame'))
        document.head.appendChild(aframeScript)
      })
    },

    // 偵測相機取得權限並實際啟動後才顯示 AR 提示
    // 需在 XR session 啟動前（a-scene 渲染前）呼叫，pipeline module 方能正確觸發
    setupCameraReadyListener() {
      const vueInstance = this
      // 防止重複設定
      if (this._cameraModuleRegistered) return

      if (typeof XR !== 'undefined' && typeof XR.addCameraPipelineModule === 'function') {
        try {
          XR.addCameraPipelineModule({
            name: 'vue-hint-controller',
            onStart: () => {
              // 追蹤中斷後重新取得時 8th Wall 會再次觸發 onStart，
              // 如果這時已經有模型在播放（_currentModelEl 還在），不能重新開放點擊，
              // 不然使用者會在同一次體驗裡疊加點出好幾個模型／去背影片同時播放
              // （多支大檔案影片同時解碼還會互相搶資源，導致畫面卡在黑幀）
              if (vueInstance._currentModelEl) {
                console.log('[XR] 相機重新啟動，但模型仍在播放中，不重新開放點擊')
                return
              }
              console.log('[XR] 相機已啟動（權限已授予），顯示 AR 提示')
              vueInstance.isShowHint = true
              vueInstance.isCanTap = true
            }
          })
          this._cameraModuleRegistered = true
          console.log('[XR] 相機就緒監聽已設定（camera pipeline module）')
          return
        } catch (e) {
          console.warn('[XR] camera pipeline module 設定失敗，採用延遲 fallback：', e)
        }
      }

      // fallback：XR 不可用或已啟動（例如 SPA 頁面重訪），延遲後直接顯示
      console.log('[XR] fallback：延遲顯示提示')
      setTimeout(() => {
        if (vueInstance._currentModelEl) return // 同上，模型還在播放就不重新開放點擊
        vueInstance.isShowHint = true
        vueInstance.isCanTap = true
      }, 2000)
    },

    // 設置 XRExtras 事件監聽
    // 初始化 tap-place 組件
    initTapPlaceComponent() {
      if (typeof AFRAME === 'undefined') {
        console.warn('AFRAME is not defined yet, retrying...')
        setTimeout(() => this.initTapPlaceComponent(), 200)
        return
      }

      try {
        // 保存 Vue 實例的引用，讓 A-Frame 組件可以訪問
        const vueInstance = this

        // 檢查組件是否已註冊
        if (AFRAME.components['tap-place']) {
          console.log('tap-place component already registered, rebinding events')

          // 等待場景載入後重新綁定事件
          const scene = this.$refs.arScene
          if (scene && scene.hasLoaded) {
            this.setupTapListener(scene, vueInstance)
          } else if (scene) {
            scene.addEventListener('loaded', () => {
              this.setupTapListener(scene, vueInstance)
            }, { once: true })
          }

          // 等待相機取得權限並啟動後才顯示提示
          this.setupCameraReadyListener()

          setTimeout(() => {
            this.isShowIndexBtn = true
            this.isShowMapBtn = true
            this.isShowDataBtn = true
          }, 1000);
          return
        }

        // 註冊 tap-place 組件
        AFRAME.registerComponent('tap-place', {
          init() {
            console.log('tap-place component initialized')
            const scene = this.el

            // 等待場景完全載入
            scene.addEventListener('loaded', () => {
              console.log('Scene loaded, setting up tap listener')
              vueInstance.setupTapListener(scene, vueInstance)
            })
          },
        })
        console.log('tap-place component registered successfully')
      } catch (error) {
        console.error('Error registering tap-place component:', error)
      }
    },

    // 設置點擊監聽器（提取出來以便重用）
    setupTapListener(scene, vueInstance) {
      console.log('Setting up tap listener')

      setTimeout(() => {
        vueInstance.isShowIndexBtn = true
        vueInstance.isShowMapBtn = true
        vueInstance.isShowDataBtn = true
      }, 1500);
    },

    // 點擊 hand-cursor 時觸發：以 THREE.js raycaster 求 ground 交點後放置模型
    onHandCursorClick(event) {
      if (!this.isCanTap) {
        console.log('Model is hidden, ignoring tap.')
        return
      }
      if (this.isModelLoading) {
        console.log('Model is loading, please wait.')
        return
      }

      const scene = this.$refs.arScene
      if (!scene) return

      const cameraEl = document.getElementById('camera')
      const ground = document.getElementById('ground')
      if (!cameraEl || !ground) return

      // 取 hand-cursor 中心的螢幕座標
      const cursorEl = event.currentTarget
      const rect = cursorEl.getBoundingClientRect()
      const screenX = rect.left + rect.width / 2
      const screenY = rect.top + rect.height / 2

      // 換算成 NDC（Normalized Device Coordinates）
      const canvas = scene.canvas
      const canvasRect = canvas.getBoundingClientRect()
      const ndcX = ((screenX - canvasRect.left) / canvasRect.width) * 2 - 1
      const ndcY = ((screenY - canvasRect.top) / canvasRect.height) * -2 + 1

      // 用 THREE.Raycaster 投射射線
      const THREE = AFRAME.THREE
      const raycaster = new THREE.Raycaster()
      const cameraObj = cameraEl.getObject3D('camera')
      raycaster.setFromCamera({ x: ndcX, y: ndcY }, cameraObj)

      const groundMesh = ground.getObject3D('mesh')
      if (!groundMesh) {
        console.warn('[HandCursor] Ground mesh 尚未就緒')
        return
      }

      const intersects = raycaster.intersectObject(groundMesh, true)
      if (!intersects.length) {
        console.warn('[HandCursor] 未命中 ground，無法放置模型')
        return
      }

      this.isShowHint = false
      this.placeModelAt(intersects[0].point, scene)
    },

    // 在指定世界座標放置 3D 模型並播放動畫（模型資料透過 WebAssembly 管道處理）
    async placeModelAt(touchPoint, scene) {
      const vueInstance = this

      vueInstance.isCanTap = false
      vueInstance.isModelLoading = true
      vueInstance.isLoading = true

      console.log('Touch point:', touchPoint)

      // ── WebAssembly 模型載入管道 ──────────────────────────────────
      // 優先使用預先載入的 Blob URL（與原本 a-asset-item 預載效果相同，零延遲）
      // 若預載尚未完成，fallback 為即時 fetch（含 WASM 處理或直通）
      let modelSrc = vueInstance._preloadedBlobUrl || vueInstance.currentModelUrl
      if (!vueInstance._preloadedBlobUrl) {
        console.warn('[WASM] 預載尚未完成，改為即時 fetch 模型')
        try {
          const buffer = await fetchAndProcessModel(
            vueInstance.currentModelUrl,
            vueInstance.isWasmReady ? vueInstance.wasmModuleUrl : null
          )
          if (vueInstance._modelBlobUrl) URL.revokeObjectURL(vueInstance._modelBlobUrl)
          const blob = new Blob([buffer], { type: 'model/gltf-binary' })
          modelSrc = URL.createObjectURL(blob)
          vueInstance._modelBlobUrl = modelSrc
        } catch (error) {
          console.warn('[WASM] 即時 fetch 失敗，降級使用原始 URL：', error)
          modelSrc = vueInstance.currentModelUrl
        }
      } else {
        console.log('[WASM] 使用預載 Blob URL，點擊延遲最小化')
      }
      // ─────────────────────────────────────────────────────────────

      const newElement = document.createElement('a-entity')

      // 計算模型朝向相機的角度，同時算出相機到落點的距離供補償使用
      const camera = document.getElementById('camera')
      const cameraPos = camera.object3D.position
      const dx = cameraPos.x - touchPoint.x
      const dy = cameraPos.y - touchPoint.y
      const dz = cameraPos.z - touchPoint.z
      let angleToCamera = Math.atan2(dx, dz) * (180 / Math.PI)

      // 距離補償係數：落點越遠模型等比放大，視覺大小趨於一致
      // 限制在 0.4–2.5 倍之間避免極端值
      const distToTap = Math.sqrt(dx * dx + dy * dy + dz * dz)
      const distanceScale = Math.max(0.4, Math.min(2.5, distToTap / SCALE_REFERENCE_DISTANCE))

      const spotData = vueInstance.spotDataManager[vueInstance.currentSpotData]
      const rotOverride = vueInstance.devRotationOverrides[vueInstance.currentSpotData]
      const effectiveRotationOffset = rotOverride !== undefined
        ? Number(rotOverride)
        : (spotData?.rotationOffset || 0)
      angleToCamera += effectiveRotationOffset

      // 套用 modelOffset（以相機朝向為基準旋轉，offset 在模型的 local space 中定義）
      const modelOffsetStr = spotData?.modelOffset || '0 0 0'
      const [mx, my, mz] = modelOffsetStr.split(' ').map(Number)
      const modelAngleRad = angleToCamera * Math.PI / 180
      const modelPosition = {
        x: touchPoint.x + mx * Math.cos(modelAngleRad) + mz * Math.sin(modelAngleRad),
        y: touchPoint.y + (my || 0),
        z: touchPoint.z - mx * Math.sin(modelAngleRad) + mz * Math.cos(modelAngleRad)
      }

      vueInstance._baseModelPosition = { ...modelPosition }
      vueInstance._baseAngleToCamera = angleToCamera
      newElement.setAttribute('position', modelPosition)
      newElement.setAttribute('rotation', `0 ${angleToCamera} 0`)
      newElement.setAttribute('visible', 'false')
      newElement.setAttribute('gltf-model', modelSrc)
      newElement.setAttribute('no-cull', '')

      scene.appendChild(newElement)
      console.log('Tree entity created and added to scene')

      newElement.addEventListener('model-loaded', () => {
        console.log('Model loaded')

        requestAnimationFrame(() => {
          const spotData = vueInstance.spotDataManager[vueInstance.currentSpotData]
          vueInstance._currentModelEl = newElement
          vueInstance._currentDistanceScale = distanceScale
          vueInstance.devCurrentRotation = effectiveRotationOffset
          const override = vueInstance.devScaleOverrides[vueInstance.currentSpotData]
          if (override !== undefined) {
            vueInstance.devCurrentScale = Number(override)
            const s = vueInstance.devCurrentScale * distanceScale
            newElement.setAttribute('scale', `${s} ${s} ${s}`)
          } else {
            const baseScale = (spotData?.scale || '7 7 7').split(' ').map(Number)
            vueInstance.devCurrentScale = baseScale[0]
            newElement.setAttribute('scale', baseScale.map(s => s * distanceScale).join(' '))
          }

          const offsetOverride = vueInstance.devOffsetOverrides[vueInstance.currentSpotData]
          if (offsetOverride) {
            vueInstance.devCurrentOffset = { ...offsetOverride }
            const base = vueInstance._baseModelPosition
            newElement.setAttribute('position', {
              x: base.x + offsetOverride.x,
              y: base.y + offsetOverride.y,
              z: base.z + offsetOverride.z
            })
          } else {
            const baseOffset = (spotData?.modelOffset || '0 0 0').split(' ').map(Number)
            vueInstance.devCurrentOffset = { x: baseOffset[0] || 0, y: baseOffset[1] || 0, z: baseOffset[2] || 0 }
          }

          const videoOffsetOverride = vueInstance.devVideoOffsetOverrides[vueInstance.currentSpotData]
          if (videoOffsetOverride) {
            vueInstance.devCurrentVideoOffset = { ...videoOffsetOverride }
          } else {
            const baseVideoOffset = (spotData?.videoOffset || '1 0 0').split(' ').map(Number)
            vueInstance.devCurrentVideoOffset = { x: baseVideoOffset[0], y: baseVideoOffset[1] || 0, z: baseVideoOffset[2] || 0 }
          }

          newElement.setAttribute('sound', {
            src: '#animationSound',
            autoplay: false,
            volume: 10
          })

          // 直接呼叫 startAnimation，不等待 sound.loaded
          // （8frame 對 <audio> 元素可能永遠不觸發 loaded，會造成無限等待）
          const startAnimation = () => {
            const model = newElement.getObject3D('mesh')
            if (model) {
              console.log('[Model] 節點與材質/貼圖檔名：')
              model.traverse((child) => {
                if (child.isMesh) {
                  const materials = Array.isArray(child.material) ? child.material : [child.material]

                  // 名為 hider 的網格作為遮罩：只寫入深度緩衝區、不渲染顏色，
                  // 讓它遮住後方的模型部位
                  if (child.name.toLowerCase() === 'hider' || child.name.toLowerCase() === 'ancient_house_skeleton') {
                    materials.forEach((mat) => {
                      mat.colorWrite = false
                      mat.depthWrite = true
                    })
                    child.renderOrder = -1
                  }

                  materials.forEach((mat) => {
                    const textureNames = Object.keys(mat)
                      .filter((key) => mat[key] && mat[key].isTexture)
                      .map((key) => `${key}=${mat[key].name || mat[key].image?.src?.split('/').pop() || '(未命名)'}`)
                    // 附上目前的 material.color（如果有），方便挑目標 mesh 時知道原本的顏色，
                    // 之後要在指定時段改色才有基準值可以對照
                    const colorHex = mat.color ? `#${mat.color.getHexString()}` : 'N/A'
                    console.log(`  Mesh: ${child.name || '(未命名)'} | Material: ${mat.name || '(未命名)'} | Color: ${colorHex}${textureNames.length ? ' | Textures: ' + textureNames.join(', ') : ''}`)
                  })
                } else {
                  console.log(`  Node: ${child.name || '(未命名)'}`)
                }
              })
            }
            if (model && model.animations && model.animations.length > 0) {
              console.log(`Found ${model.animations.length} animations in model`)

              model.traverse((child) => {
                if (child.isMesh) child.frustumCulled = false
              })

              newElement.setAttribute('animation-mixer', {
                clip: '*',
                loop: 'once',
                clampWhenFinished: true,
                repetitions: 1,
                timeScale: 1
              })

              newElement.addEventListener('animation-finished', () => {
                console.log('Animation finished, hiding model and showing finish page')

                if (vueInstance._spotVideoTimeout) {
                  clearTimeout(vueInstance._spotVideoTimeout)
                  vueInstance._spotVideoTimeout = null
                }
                vueInstance.removeSpotVideo()
                vueInstance.removeAllChromaVideoLayers()
                vueInstance.clearTextureChanges()

                const soundComponent = newElement.components.sound
                if (soundComponent) soundComponent.stopSound()

                newElement.setAttribute('animation', {
                  property: 'scale',
                  to: '0 0 0',
                  dur: 1000,
                  easing: 'easeInQuad'
                })

                setTimeout(() => {
                  newElement.setAttribute('visible', 'false')
                  if (newElement.parentNode) newElement.parentNode.removeChild(newElement)
                  vueInstance._currentModelEl = null
                  vueInstance._currentDistanceScale = 1
                  vueInstance.isShowFinalPage = true
                }, 1000)
              })

              requestAnimationFrame(() => {
                newElement.setAttribute('visible', 'true')

                const soundComponent = newElement.components.sound
                if (soundComponent) {
                  soundComponent.playSound()
                  console.log('Animation sound started')
                }

                console.log('GLB animation started')
                vueInstance.trackGAEvent()

                vueInstance.isLoading = false
                vueInstance.isModelLoading = false

                // 動畫播放至指定秒數時，於模型旁播放插播影片（不影響 AR 動畫/音效）
                if (spotData?.videoUrl && spotData?.videoTriggerTime != null) {
                  vueInstance._spotVideoTimeout = setTimeout(() => {
                    vueInstance.playSpotVideo(spotData)
                  }, spotData.videoTriggerTime * 1000)
                }

                // 多層去背影片（背景樹＋依序出現的行人），依 config 的 startAt 各自排程
                if (Array.isArray(spotData?.chromaVideos) && spotData.chromaVideos.length) {
                  vueInstance.setupChromaVideos(spotData.chromaVideos)
                }


                // 動畫播放至指定秒數時，把指定名稱的貼圖換成新圖片
                if (Array.isArray(spotData?.textureChanges) && spotData.textureChanges.length) {
                  vueInstance.setupTextureChanges(model, spotData.textureChanges)
                }
              })
            } else {
              console.warn('No animations found in GLB model')
              newElement.setAttribute('visible', 'true')
              vueInstance.isLoading = false
              vueInstance.isModelLoading = false
            }
          }

          startAnimation()
        })
      })
    },

  }
}
</script>
<style scoped>
body {
  height: 100vh !important;
  overflow: hidden !important;
}

#main_page {
  height: 100svh;
  overflow: hidden;
  position: relative;
}

/* 載入中畫面 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-overlay p {
  color: white;
  font-size: 1.2rem;
  margin-top: 20px;
}

.en {
  letter-spacing: 0px !important;
}

.hint {
  transform: translate(-50%, -50%);
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 1000;
  pointer-events: none;
}

.hint img {
  height: 100svh;
  display: block;
}

.hintword {
  position: absolute;
  top: 0px;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 1rem;
  width: 100%;
}

.hintword.circle1 {
  left: 28%;
  top: 83%;
}

.hintword.circle2 {
  left: 51%;
  top: 65%;
}

.hintword.circle3 {
  left: 53%;
  top: 81%;
}
.hintword.circle4 {
  left: 54%;
  top: 63%;
}
.hintword.circle5 {
  left: 57%;
  top: 74%;
}
.hintword.circle6 {
    top: 86%;
    left: 29%;
}
.hintword.circle7 {
  left: 65%;
  top: 88%;
}
.hintword.circle8 {
  left: 50%;
  top: 90%;
}
.hintword.circle9 {
  left: 50%;
  top: 93%;
}
.hintword.circle10 {
  left: 50%;
  top: 81%;
}
.hintword.circle11 {
  left: 52%;
  top: 80%;
}
.hintword.circle12 {
  left: 50%;
  top: 84%;
}
.hintword.circle13 {
  left: 75%;
  top: 60%;
}
.hintword.circle14 {
  left: 52%;
  top: 73%;
}
.hintword.circle15 {
  left: 55%;
  top: 89%;
}

.VitexNigraCursor, .MangoCursor, .PaperMulberryCursor, .SpinyBambooCursor,
.ChinaberryCursor, .ArecaCursor, .ParasolleaftreeCursor, .TaiwanFlamegoldCursor,
.BarringtoniaasiaticaCursor, .TaiwancrapemyrtleCursor, .SoapberryCursor, .LargeLeavedNanmuCursor,
.ErythrinaCursor, .FicusCursor, .ImperataCursor {
  position: absolute;
  width: 25%;
  top: 64%;
  left: 30%;
}

.hand-cursor-gif {
  display: block;
  width: 100%;
  height: unset !important;
}

/* 實際可點擊區域：置中的圓形，比整張 gif 小很多，數字改這裡就好（相對於 hand-cursor-wrap 寬度的百分比） */
.hand-cursor-hit {
  position: absolute;
  top: 25%;
  left: 45%;
  transform: translate(-50%, -50%);
  width: 35%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  pointer-events: auto;
  cursor: pointer;
}
.VitexNigraCursor { top:67%;left: 8%; }
.MangoCursor { top: 48%; left: 37%; }
.PaperMulberryCursor { top: 64%; left: 40%; }
.SpinyBambooCursor { top: 46%; left: 40%; }
.ChinaberryCursor { top: 58%; left: 44%; }
.ArecaCursor { top: 70%;left: 14%; } 
.ParasolleaftreeCursor { top: 72%; left: 53%; }
.TaiwanFlamegoldCursor { top: 73%; left: 36%; }
.BarringtoniaasiaticaCursor { top: 77%; left: 37%; }
.TaiwancrapemyrtleCursor { top: 62%; left: 38%; }
.SoapberryCursor { top: 56%; left: 40%; }
.LargeLeavedNanmuCursor { top: 68%; left: 38%; }
.FicusCursor { top: 73%; left: 42%; }
.ErythrinaCursor { top: 40%; left: 70%; }
.ImperataCursor { top: 56%; left: 38%; }

/* Model Loading */
.model-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.model-loading .loading-spinner {
  width: 60px;
  height: 60px;
  border: 6px solid rgba(255, 255, 255, 0.3);
  border-top-color: #436ab2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.model-loading p {
  color: white;
  font-size: 1.2rem;
  margin-top: 15px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-weight: bold;
}

.hint p {
  margin: 0px;
  text-align: left;
}

.cross-btn {
  position: absolute;
  top: 0px;
  right: 0px;
  font-size: 25px;
  cursor: pointer;
  background-color: rgba(153, 153, 153, 0.7);
  ;
  color: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.9);
  transform: translate(40%, -30%);
}

.cover-img {
  position: absolute;
  width: 50px;
  height: 50px;
  top: 0;
  left: 0;
}

/* topic */
.topic {
  background-color: royalblue;
  text-align: center;
  padding: 4%;
}

.topic img {
  width: 80%;
  height: auto;
  display: block;
  margin: auto;
}

/* ================= */
/* A-Frame AR Scene */
a-scene {
  width: 100%;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
}

/* ================= */
.finish-page {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.7);
  transform: translate(-50%, -50%);
  left: 50%;
  width: 90%;
  top: 45%;
  border-radius: 15px;
}

.finish-page .finish-content {
  width: 90%;
  margin: auto;
  text-align: center;
  padding-top: 30px;
}

.finish-page .finish-content h1 {
  letter-spacing: 2px;
  font-size: 1.6rem;
}

.finish-page .finish-content .btn_area {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
}

.finish-page .finish-content .btn_css {
  position: relative;
  width: 50%;
  margin: 3% auto 7% auto;
  border-radius: 45px;
  border: 1px solid rgba(255, 255, 255, 1);
  background: rgba(67, 106, 178, 0.7);
  padding: 6px 0px;
  max-width: 200px;
  font-size: 1.4rem;
  overflow: hidden;
}

.finish-page .finish-content #btn_cover {
  position: absolute;
  left: -5%;
  top: 0px;
  width: 110%;
  z-index: 1000;
  pointer-events: none;
}

/* Tablet 響應式設計 (768px - 1024px) */
@media screen and (min-width: 768px) and (max-width: 1024px) {
  .cross-btn {
    width: 70px;
    height: 70px;
    font-size: 28px;
    transform: translate(30%, -25%);
  }

  .cover-img {
    width: 70px;
    height: 70px;
  }

  .finish-page {
    width: 85%;
    border-radius: 20px;
  }

  .finish-page .finish-content {
    padding-top: 40px;
  }

  .finish-page .finish-content h1 {
    font-size: 2rem;
  }

  .finish-page .finish-content .btn_css {
    font-size: 1.6rem;
    padding: 10px 0px;
    border-radius: 50px;
    max-width: 250px;
  }

  .hintword {
    font-size: 1.6rem;
  }
}

/* Large Tablet 響應式設計 (1025px - 1366px) */
@media screen and (min-width: 1025px) and (max-width: 1366px) {
  .cross-btn {
    width: 70px;
    height: 70px;
    font-size: 32px;
    transform: translate(25%, -20%);
  }

  .cover-img {
    width: 70px;
    height: 70px;
  }

  .finish-page {
    width: 80%;
    border-radius: 25px;
  }

  .finish-page .finish-content {
    padding-top: 50px;
  }

  .finish-page .finish-content h1 {
    font-size: 2.3rem;
  }

  .finish-page .finish-content .btn_css {
    font-size: 1.8rem;
    padding: 12px 0px;
    border-radius: 55px;
    max-width: 280px;
  }

  .hintword {
    font-size: 1.8rem;
  }
}

/* Landscape Tablet 響應式設計 */
@media screen and (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  .finish-page .finish-content h1 {
    font-size: 1.8rem;
  }

  .finish-page .finish-content .btn_css {
    font-size: 1.4rem;
    max-width: 220px;
  }

  .hintword {
    font-size: 1.4rem;
  }
}

/* ── 開發者模式 ── */
.dev-toggle {
  position: fixed;
  top: 80px;
  left: 10px;
  z-index: 10000;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
}

.dev-panel {
  position: fixed;
  top: 120px;
  left: 10px;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.78);
  color: white;
  border-radius: 12px;
  padding: 12px;
  min-width: 230px;
  font-size: 13px;
  max-height: 50vh;
  overflow-y: auto;
}

.dev-header {
  font-weight: bold;
  font-size: 11px;
  color: #aaa;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.dev-label {
  font-size: 10px;
  color: #888;
  margin-bottom: 3px;
}

.dev-scale-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
}

.dev-value {
  flex: 1;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
}

.dev-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 14px;
  cursor: pointer;
  touch-action: manipulation;
  line-height: 1;
}

.dev-btn:active {
  background: rgba(255, 255, 255, 0.3);
}

.dev-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.dev-save-btn {
  background: rgba(67, 106, 178, 0.7);
  flex: 1;
}

.dev-saved-msg {
  color: #66bb6a;
  font-size: 12px;
  margin-bottom: 6px;
}

.dev-export-text {
  width: 100%;
  height: 130px;
  background: rgba(0, 0, 0, 0.6);
  color: #80cbc4;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 10px;
  font-family: monospace;
  resize: none;
  box-sizing: border-box;
  padding: 6px;
  margin-top: 4px;
}
</style>
